/**
 * The `results` data layer — thin hooks bound to the REAL server endpoints (server/src/modules/
 * results/routes.js, all under /api/v1). Server state lives only in TanStack Query, lab-scoped via
 * qk.scoped(labId, "results", ...). Mutations invalidate the affected CR so the preview + diffusion
 * board reflect the new etat/canal immediately (L-1 CRUD-complete).
 *
 *   GET  /results/comptes-rendus/:id                  -> CompteRendu
 *   POST /results/demandes/:id/emettre   (no body)    -> EmissionResult   (type derived server-side, VAL-6)
 *   POST /results/canaux/:id/delivrer    (no body)    -> CanalDiffusion   (VAL-8 hold derived server-side)
 *   POST /results/canaux/:id/liberer                  -> CanalDiffusion
 *   GET  /results/comptes-rendus/:id/imprimer         -> raw text/html (NOT the JSON envelope)
 */
import { useCallback, useEffect, useState } from "react";
import { api, useApiMutation, useApiQuery, qk, API_BASE, tokenStore } from "@/api";
import { useAuthStore } from "@/auth";
import type {
  ChannelActionResult,
  CompteRendu,
  EmissionResult,
  PendingEmissionPage,
  RecallLine,
  RecallRecoverResult,
} from "./types";

/** The current lab id (drives the lab-scoped query keys; null before login). */
function useLabId(): string | null {
  return useAuthStore((s) => s.user?.laboratoireId ?? s.lab?.id ?? null);
}

/** GET /results/comptes-rendus/:id — the full CR (lignes + canaux). */
export function useCompteRendu(compteRenduId: string | null) {
  const labId = useLabId();
  const query = useApiQuery<CompteRendu>(
    qk.scoped(labId, "results", "compte-rendu", compteRenduId ?? "_"),
    `/results/comptes-rendus/${compteRenduId ?? ""}`,
    { enabled: !!compteRenduId },
  );
  return { ...query, labId };
}

/**
 * POST /results/demandes/:id/emettre — emit a (progressive) compte-rendu from a demande. NOVA-057: the
 * report TYPE (partiel/complet) is DERIVED server-side from the validated-result coverage (VAL-6) — the
 * client sends NO scope; the mutation takes no variables.
 */
export function useEmitCompteRendu(demandeAnalyseId: string | null) {
  const labId = useLabId();
  return useApiMutation<EmissionResult, void>(
    () => api.post<EmissionResult>(`/results/demandes/${demandeAnalyseId ?? ""}/emettre`, undefined),
    {
      invalidate: [
        qk.scoped(labId, "results", "compte-rendu"),
        qk.scoped(labId, "results", "demande", demandeAnalyseId ?? "_"),
      ],
    },
  );
}

/**
 * POST /results/canaux/:id/delivrer — deliver one channel. NOVA-055: the VAL-8 patient-channel hold is
 * DERIVED SERVER-SIDE from the demande's open escalations — the client sends NO `has_critical_unacked`
 * flag (a held channel simply comes back `statut: "retenu"`, which the board banners).
 */
export function useDeliverChannel(compteRenduId: string | null) {
  const labId = useLabId();
  return useApiMutation<ChannelActionResult, { canalId: string }>(
    (vars) => api.post<ChannelActionResult>(`/results/canaux/${vars.canalId}/delivrer`, undefined),
    {
      invalidate: [
        qk.scoped(labId, "results", "compte-rendu", compteRenduId ?? "_"),
      ],
    },
  );
}

/** POST /results/canaux/:id/liberer — release a held (retenu) channel early. */
export function useReleaseChannel(compteRenduId: string | null) {
  const labId = useLabId();
  return useApiMutation<ChannelActionResult, { canalId: string }>(
    (vars) => api.post<ChannelActionResult>(`/results/canaux/${vars.canalId}/liberer`, undefined),
    {
      invalidate: [
        qk.scoped(labId, "results", "compte-rendu", compteRenduId ?? "_"),
      ],
    },
  );
}

/**
 * GET /results/comptes-rendus/:id/imprimer — returns RAW text/html (the only non-envelope route).
 * We fetch it through the same proxy with the Bearer token so it honors the print capability + lab
 * scope, then surface the markup for an in-app iframe preview (no clinical value leaves the box).
 */
export function usePrintArtifact(compteRenduId: string | null, enabled: boolean) {
  const [state, setState] = useState<{
    loading: boolean;
    error: string | null;
    html: string | null;
  }>({ loading: false, error: null, html: null });

  const load = useCallback(async () => {
    if (!compteRenduId) return;
    setState({ loading: true, error: null, html: null });
    try {
      const token = tokenStore.getAccessToken();
      const res = await fetch(`${API_BASE}/results/comptes-rendus/${compteRenduId}/imprimer`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const text = await res.text();
      if (!res.ok) {
        // The print route emits a bare { success:false, code, message } on 404 (not HTML).
        let message = `Erreur ${res.status}`;
        try {
          const parsed = JSON.parse(text) as { message?: string };
          if (parsed.message) message = parsed.message;
        } catch {
          /* non-JSON body — keep the status message */
        }
        setState({ loading: false, error: message, html: null });
        return;
      }
      setState({ loading: false, error: null, html: text });
    } catch (err) {
      setState({
        loading: false,
        error: err instanceof Error ? err.message : "Erreur réseau",
        html: null,
      });
    }
  }, [compteRenduId]);

  useEffect(() => {
    if (enabled && compteRenduId) void load();
    if (!enabled) setState({ loading: false, error: null, html: null });
  }, [enabled, compteRenduId, load]);

  return { ...state, reload: load };
}

/** Build the authenticated print URL (used for the "open in new tab" affordance). */
export function printUrl(compteRenduId: string): string {
  return `${API_BASE}/results/comptes-rendus/${compteRenduId}/imprimer`;
}

// ── LAB-08 — the "à émettre" worklist (validated demandes with no/stale compte-rendu) ────────────
/**
 * GET /results/pending — the server-backed emission queue: validated demandes awaiting a (fresh) CR.
 * Lets a biologiste see the worklist without knowing a numeric id (the console was id-entry only).
 * `enabled` folds the emit capability in at the call site (only an emitter needs the queue). Lab-scoped;
 * a short staleTime keeps it warm without hammering. NOTE (residual): the endpoint is novanalyse-server
 * (Stream B) territory — the contract here is the client's expectation (validated-not-emitted).
 */
export function usePendingEmissions(enabled: boolean) {
  const labId = useLabId();
  return useApiQuery<PendingEmissionPage>(
    qk.scoped(labId, "results", "pending"),
    "/results/pending",
    { enabled, staleTime: 15_000 },
  );
}

// ── NOVA-054 — the comptoir desk-recall worklist (D-24) ─────────────────────────
function recallKey(labId: string | null) {
  return qk.scoped(labId, "results", "recall");
}

/** GET /results/recall — the open desk-recall markers (a superseded copy was handed; recall it). */
export function useRecallWorklist() {
  const labId = useLabId();
  return useApiQuery<RecallLine[]>(recallKey(labId), "/results/recall");
}

/** POST /results/recall/:id/recuperer — acknowledge a recovered desk copy (the corrected report re-delivered). */
export function useRecoverRecall() {
  const labId = useLabId();
  return useApiMutation<RecallRecoverResult, { canalId: string }>(
    (vars) => api.post<RecallRecoverResult>(`/results/recall/${vars.canalId}/recuperer`, undefined),
    {
      invalidate: [
        recallKey(labId),
        qk.scoped(labId, "results", "compte-rendu"),
      ],
    },
  );
}
